/**
 * API client for the member area.
 *
 * No offline queue, no IndexedDB cache — unlike the field app, nobody is
 * filling this out standing in a bed with one bar of signal. It is a simple
 * fetch wrapper carrying a bearer session token in local storage; see
 * services/api/src/member-auth.ts for why a token, not a cookie.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;
const SESSION_KEY = 'ff-member.session';

export function getSession(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setSession(token: string): void {
  localStorage.setItem(SESSION_KEY, token);
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = getSession();
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body) headers.set('content-type', 'application/json');
  if (session && path.startsWith('/member/')) headers.set('authorization', `Bearer ${session}`);

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (response.status === 401) {
    // /member/v1/* 401s mean the bearer session itself is bad — clear it so a
    // stale token doesn't keep getting sent. /public/v1/member/redeem also
    // answers 401 for a link that's expired or was already used, which is a
    // different, more specific story the server already tells well; don't
    // paper over it with the generic "session expired" copy.
    if (path.startsWith('/member/')) {
      clearSession();
      throw new ApiError('Your session has expired. Use the link from your email again.', 401);
    }
    const detail = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(detail?.message ?? 'That link has expired or was already used.', 401);
  }

  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(detail?.message ?? `Something went wrong (${response.status}).`, response.status);
  }

  return response.json() as Promise<T>;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}
